import { BalanceData } from "../data/balance";
import { SkinData } from "../data/commerce";
import { SupplyId, isSupplyId } from "../data/supplies";
import { ArmouryEntry } from "../data/trophies";
import { Pcg32 } from "../maths/pcg32";
import { PaidReward, payReward } from "./events";
import { metaSeed, utcDay, utcWeek } from "./orders";
import { PlayerProfile } from "./profile";
import { Wallet } from "./wallet";

/**
 * The Trophy Hall's rules (MP-23, docs/SHOP.md §9.7; data/trophies.ts): when it opens, the Treasures (shop.json skins
 * with a `trophies` unlock) and the Armoury's weekly shelf — which entries a UTC week shows (the fixed ones and
 * `rotatingSlots` rotating ones picked by the week alone, the same for every player), the supplies a pack holds
 * (picked by week, entry and purchase number, so they are shown before buying), the weekly caps, and buying, which
 * spends Trophies once and pays once (the purchase number is part of the request: a repeated request is refused).
 * Pure and deterministic: time comes in as `now` (Unix seconds).
 */

/** Pcg32 stream for the shelf's picks, apart from every other use of the same seeds. */
const ARMOURY_STREAM = 223;
/** The shelf's seed owner: the same for every player (the week alone picks). */
const ARMOURY_SEED = "armoury";
/** Room for a week's purchases of one entry in its chest seed (caps are single digits). */
const ARMOURY_SEED_SPREAD = 64;

/** The Armoury's week: the UTC week (Monday 00:00 UTC, as the ISO week) of `now`. */
export function armouryWeek(now: number): number { return utcWeek(utcDay(now)); }

/** The week's shelf: the fixed entries, then `rotatingSlots` distinct rotating ones (in their data order), by the week. */
export function armouryShelf(balance: BalanceData, week: number): ArmouryEntry[] {
  const a = balance.trophies.armoury;
  const pool = a.rotating.map((_, i) => i), rng = new Pcg32(metaSeed(ARMOURY_SEED, "rotation", week), ARMOURY_STREAM), picked: number[] = [];
  for (let k = 0; k < a.rotatingSlots && pool.length > 0; k++) picked.push(pool.splice(rng.nextInt(pool.length), 1)[0]);
  return [...a.fixed, ...picked.sort((x, y) => x - y).map((i) => a.rotating[i])];
}

/** The supplies the `n`th (0-based) purchase of a supplies pack in `week` holds (empty when the bundle has no supplies). */
export function armourySupplies(balance: BalanceData, entry: ArmouryEntry, week: number, n: number): SupplyId[] {
  const ids = balance.commerce.shop.supplies.items.map((s) => s.id).filter(isSupplyId);
  if (entry.supplies <= 0 || ids.length === 0) return [];
  const rng = new Pcg32(metaSeed(ARMOURY_SEED, `supplies:${entry.id}:${n}`, week), ARMOURY_STREAM);
  return Array.from({ length: entry.supplies }, () => ids[rng.nextInt(ids.length)]);
}

/** A skin's Trophy price, or null when it is not a Trophy Hall treasure (any other unlock). */
export function trophyPrice(skin: SkinData): number | null {
  // Read loosely: the `trophies` unlock kind is the cosmetics catalog's (MP-23 trophyart), `{ by: "trophies", price }`.
  const u = skin.unlock as { readonly by: string; readonly price?: unknown; readonly trophies?: unknown };
  if (u.by !== "trophies") return null;
  const p = u.price ?? u.trophies;
  return typeof p === "number" && Number.isInteger(p) && p > 0 ? p : null;
}

/** The Treasures: every skin with a Trophy price, cheapest first (catalog order between equal prices). */
export function trophySkins(balance: BalanceData): { readonly skin: SkinData; readonly price: number }[] {
  return balance.commerce.shop.skins.list.flatMap((skin) => { const price = trophyPrice(skin); return price === null ? [] : [{ skin, price }]; })
    .map((t, i) => ({ t, i })).sort((a, b) => a.t.price - b.t.price || a.i - b.i).map(({ t }) => t);
}

/** Whether the Hall is open: its level cleared, or Trophies held or ever met (the first Trophies open it at once). */
export function trophyHallOpen(balance: BalanceData, highestCleared: number, held: number, met: boolean): boolean {
  return highestCleared >= balance.trophies.unlockAfterLevel || held > 0 || met;
}

export interface TrophyHallJson {
  /** The UTC week the counts belong to. */
  week?: number;
  /** Armoury purchases this week by entry id. */
  bought?: Record<string, number>;
  /** The player has held Trophies (the Hall stays open; Captain Bram's first line about them has its cue). */
  met?: boolean;
}

/** The `trophies` save section: this week's Armoury purchases and whether the player has met Trophies. */
export class TrophyHallState {
  private week = -1;
  private readonly bought = new Map<string, number>();
  met = false;

  /** Purchases of `id` in `week` (0 in any other week than the counted one). */
  boughtOf(id: string, week: number): number { return week === this.week ? this.bought.get(id) ?? 0 : 0; }

  /** Counts one purchase of `id` in `week`, starting the week's counts afresh when it is a new week. */
  note(id: string, week: number): void {
    if (week !== this.week) { this.week = week; this.bought.clear(); }
    this.bought.set(id, this.boughtOf(id, week) + 1);
  }

  toJson(): TrophyHallJson {
    return { week: this.week, bought: Object.fromEntries([...this.bought].sort(([a], [b]) => (a < b ? -1 : 1))), ...(this.met ? { met: true } : {}) };
  }

  /** Restores field by field: anything malformed reads as none (a save from before the Hall has no section). */
  static fromJson(j: unknown): TrophyHallState {
    const s = new TrophyHallState();
    if (typeof j !== "object" || j === null) return s;
    const src = j as TrophyHallJson;
    if (Number.isInteger(src.week)) s.week = src.week as number;
    if (typeof src.bought === "object" && src.bought !== null) {
      for (const [k, v] of Object.entries(src.bought)) if (Number.isInteger(v) && v > 0) s.bought.set(k, v);
    }
    s.met = src.met === true;
    return s;
  }
}

/** Where an Armoury purchase pays. */
export interface ArmouryTarget {
  readonly profile: PlayerProfile;
  readonly wallet: Wallet;
  readonly playerId: string;
  /** The player's highest cleared level (chests are rolled at it; minLevel gates). */
  readonly level: number;
}

/** What an Armoury purchase paid: the reward (a chest for the caller to bank) or the supplies added. */
export interface ArmouryPaid {
  readonly entry: ArmouryEntry;
  readonly paid: PaidReward | null;
  readonly supplies: readonly SupplyId[];
}

/** Why an Armoury entry cannot be bought now ("" when it can): the shelf, its level, the cap, the stock of supplies. */
export function armouryBlock(balance: BalanceData, state: TrophyHallState, entry: ArmouryEntry, week: number, t: Pick<ArmouryTarget, "profile" | "wallet" | "level">): string {
  if (!armouryShelf(balance, week).some((e) => e.id === entry.id)) return "not this week";
  if (t.level < entry.minLevel) return "locked";
  const n = state.boughtOf(entry.id, week);
  if (n >= entry.weeklyCap) return "sold out";
  if (entry.supplies > 0) {
    const data = balance.commerce.shop.supplies, picks = armourySupplies(balance, entry, week, n);
    if (picks.length === 0 || picks.some((id) => t.profile.supplies.count(id) + picks.filter((x) => x === id).length > data.maxOwned)) return "you hold all you can carry";
  }
  return t.wallet.canAfford("trophies", entry.price) ? "" : "not enough";
}

/**
 * Buys the `n`th purchase (0-based, as shown) of Armoury entry `id` this week: spends its Trophies once and pays it.
 * Refused, changing nothing, when the entry is not on this week's shelf, locked, sold out, the shown purchase number is
 * stale ("offer changed": a repeated tap), the supplies would pass the stock cap, or the Trophies are short.
 */
export function buyArmoury(balance: BalanceData, state: TrophyHallState, id: string, n: number, now: number, t: ArmouryTarget): ArmouryPaid | string {
  const week = armouryWeek(now), entry = armouryShelf(balance, week).find((e) => e.id === id);
  if (!entry) return "not this week";
  if (state.boughtOf(id, week) !== n) return "offer changed";
  const block = armouryBlock(balance, state, entry, week, t);
  if (block !== "") return block === "not enough" ? "not enough Trophies" : block;
  const ref = `armoury.${week}.${id}.${n}`;
  const spent = t.wallet.spend("trophies", entry.price, "trophyHall", now, ref);
  if (spent !== true) return spent;
  state.note(id, week);
  if (entry.reward === null) {
    const picks = armourySupplies(balance, entry, week, n);
    for (const s of picks) t.profile.supplies.add(balance.commerce.shop.supplies, s, 1);
    return { entry, paid: null, supplies: picks };
  }
  const paid = payReward(balance, entry.reward, { profile: t.profile, wallet: t.wallet, level: t.level, seed: metaSeed(t.playerId, `armoury:${id}`, week * ARMOURY_SEED_SPREAD + n), at: now, ref });
  return { entry, paid, supplies: [] };
}

/**
 * Buys Trophy Hall treasure `skinId` (a skin with a Trophy price): spends its price once; the caller unlocks the skin.
 * Refused, changing nothing, when it is not a treasure, already owned (a skin is owned once) or the Trophies are short.
 */
export function buyTrophySkin(balance: BalanceData, wallet: Wallet, skinId: string, owns: (id: string) => boolean, now: number): true | string {
  const t = trophySkins(balance).find((x) => x.skin.id === skinId);
  if (!t) return "not a treasure";
  if (owns(skinId)) return "owned";
  return wallet.spend("trophies", t.price, "trophyHall", now, `treasure.${skinId}`);
}
